import { createStore } from 'vuex'
import { auth, db } from '@/firebase' // 기존 import 유지
import {
  doc,
  setDoc,
  getDoc,
  addDoc,
  updateDoc,
  increment,
  collection,
  serverTimestamp,
} from 'firebase/firestore' // Firestore 함수 추가 import
import { getFunctions } from 'firebase/functions' // 기존 import 유지
import { getAuth, onAuthStateChanged } from 'firebase/auth' // Firebase Auth import는 더 이상 필요 없지만, 다른 곳에서 사용될 수 있으므로 일단 유지. initializeAuth에서는 사용하지 않음.
import { GraphQLClient, gql } from 'graphql-request' // GraphQLClient 및 gql import

const store = createStore({
  state: {
    user: null, // 사용자 정보 객체를 저장할 변수
    lydia: 0, // Lydia 상태 (기존 유지)
    deductionValues: {
      // Lydia 차감 값 (기존 유지)
      hexagramToday: Number(import.meta.env.VITE_CHARGE_HEXAGRAM),
      fiveElementDaily: Number(import.meta.env.VITE_CHARGE_QIFLOW),
      fortuneThisYear: Number(import.meta.env.VITE_CHARGE_FORTUNE),
      MBTI: Number(import.meta.env.VITE_CHARGE_MBTI),
      divination: Number(import.meta.env.VITE_CHARGE_ICHING),
      downloadGua: Number(import.meta.env.VITE_CHARGE_DOWNLOAD_GUA),
      downloadAll: Number(import.meta.env.VITE_CHARGE_DOWNLOAD_ALL),
    },
    // ... 다른 state 속성이 있다면 유지
  },
  getters: {
    // 사용자 로그인 상태 확인 (user 객체 존재 여부 및 유효한 데이터 확인)
    isAuthenticated: (state) =>
      state.user !== null && state.user.accessToken !== undefined,
    getUser: (state) => state.user, // 사용자 정보 가져오기
    getLydia: (state) => state.lydia, // Lydia 상태 가져오기
    getDeductionValue: (state) => (key) => state.deductionValues[key], // 차감 값 가져오기
    // ... 다른 getters 속성이 있다면 유지
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user
      // 사용자가 설정될 때 lydia 값을 함께 설정 (Firestore에서 가져온 값 활용)
      state.lydia = user && user.lydia !== undefined ? user.lydia : 0
    },
    CLEAR_USER(state) {
      state.user = null
      state.lydia = 0 // 로그 아웃 또는 사용자 정보 초기화 시 lydia 값 리셋
    },
    SET_LYDIA(state, value) {
      state.lydia = value
    },
    DECREMENT_LYDIA(state, amount) {
      state.lydia -= amount
    },
    UPDATE_USER_DATA(state, data) {
      if (state.user) {
        state.user = { ...state.user, ...data }
        // Lydia 값 업데이트 시 state.lydia도 함께 업데이트
        if (data.lydia !== undefined) {
          state.lydia = data.lydia
        }
      }
    },
    REMOVE_USER_FIELDS(state, fields) {
      if (state.user) {
        for (const field of fields) {
          if (state.user.hasOwnProperty(field)) {
            delete state.user[field]
          }
        }
      }
    },
  },

  actions: {
    // 사용자 정보 초기화 및 설정 (액세스 코드 기반)
    async initializeAuth({ commit, dispatch }) {
      console.log('Initializing Auth...')

      try {
        // localStorage에서 액세스 코드 가져오기
        const accessToken = localStorage.getItem('accessToken')

        if (accessToken) {
          console.log('액세스 코드 발견:', accessToken)

          // Firestore에서 해당 액세스 코드로 사용자 문서 조회
          const userRef = doc(db, 'users', accessToken)
          const userDoc = await getDoc(userRef)

          if (userDoc.exists()) {
            const userData = userDoc.data()
            console.log('Firestore 사용자 정보 로드 성공:', userData)

            commit('SET_USER', { accessToken, ...userData })

            console.log('사용자 초기 lydia:', userData.lydia)
          } else {
            console.warn(
              'Firestore에 해당하는 사용자 문서가 없습니다:',
              accessToken,
            )
            // 유효하지 않은 액세스 코드 처리 (localStorage에서 제거)
            localStorage.removeItem('accessToken')
            commit('CLEAR_USER') // 사용자 정보 초기화
          }
        } else {
          console.log('localStorage에 액세스 코드가 없습니다.')
          commit('CLEAR_USER') // 사용자 정보 초기화
        }
      } catch (error) {
        console.error('사용자 정보 초기화 중 오류 발생:', error.message)
        commit('CLEAR_USER') // 오류 발생 시 사용자 정보 초기화
      } finally {
        console.log('Auth Initialization Complete.')
      }
    },

    async getUserSnapshot({ state }) {
      const accessToken = state.user?.accessToken || localStorage.getItem('accessToken')
      const docRef = doc(db, 'users', accessToken)
      const snapshot = await getDoc(docRef)
      return snapshot.exists() ? snapshot.data() : null
    },
    
    async chargeLydia(
      { commit, state },
      { userName, accessToken, amount, chargeInfo },
    ) {
      console.log('리워드/충전 시도합니다. 금액은 ', amount)
      const graphQLClient = new GraphQLClient(import.meta.env.VITE_API_ENDPOINT)
      const mutation = gql`
        mutation ChargeLydia($input: ChargeLydiaInput!) {
          chargeLydia(input: $input) {
            result
            message
            newLydiaBalance
          }
        }
      `

      try {
        const variables = {
          input: {
            userName,
            accessToken,
            amount: Number(amount),
            chargeInfo: JSON.stringify(chargeInfo),
          },
        }

        const response = await graphQLClient.request(mutation, variables)
        const result = response.chargeLydia

        if (result.result) {
          const updatedUser = { ...state.user, lydia: result.newLydiaBalance }
          commit('SET_USER', updatedUser)
          localStorage.setItem('user', JSON.stringify(updatedUser))
          return { success: true, message: result.message }
        } else {
          return {
            success: false,
            message: result.message || 'Lydia 충전에 실패했습니다.',
          }
        }
      } catch (error) {
        console.error('chargeLydia error:', error)
        return { success: false, message: 'Lydia 충전 중 오류가 발생했습니다.' }
      }
    },

    // Lydia 차감 (API 호출 방식으로 변경)
    async deductLydia(
      { commit, state, dispatch },
      { menuKey, deductionAmount },
    ) {
      console.log(`Attempting to deduct ${deductionAmount} for ${menuKey}`)
      // 사용자 객체가 유효하고 lydia 정보가 있는지 확인
      if (state.user && state.lydia >= deductionAmount) {
        const accessToken = state.user.accessToken
        if (!accessToken) {
          console.error(
            '사용자 accessToken을 찾을 수 없습니다. Lydia 차감 실패.',
          )
          return false
        }

        // GraphQL Mutation 정의
        const DEDUCT_LYDIA_MUTATION = gql`
          mutation DeductLydia($input: DeductLydiaInput!) {
            deductLydia(input: $input) {
              result
              message
              newLydiaBalance
            }
          }
        `

        // GraphQL Client 인스턴스 생성 (또는 기존 인스턴스 사용)
        // TODO: 환경 변수에서 GraphQL API URL 가져오기
        const endpoint = import.meta.env.VITE_API_ENDPOINT // <-- GraphQL API 엔드포인트 URL을 여기에 입력하세요.
        const client = new GraphQLClient(endpoint)

        try {
          const variables = {
            input: {
              accessToken: accessToken,
              amount: Number(deductionAmount),
              menuUsed: menuKey, // API 스펙에 맞게 menuUsed로 전달
            },
          }

          console.log('Deducting Lydia via API...', variables)
          const data = await client.request(DEDUCT_LYDIA_MUTATION, variables)

          if (data && data.deductLydia && data.deductLydia.result) {
            // API 호출 성공 시 로컬 상태 업데이트
            commit('DECREMENT_LYDIA', deductionAmount)
            console.log(
              `Lydia deducted via API: ${deductionAmount}. Remaining lydia (local): ${state.lydia}`,
            )
            return true // 차감 성공
          } else {
            console.error('Lydia 차감 API 호출 실패:', data.deductLydia.message)
            // API 호출은 성공했으나 서버에서 실패 응답 반환
            return false // 차감 실패
          }
        } catch (error) {
          console.error('Lydia 차감 API 호출 중 오류 발생:', error)
          return false
        }
      } else {
        console.warn(
          `${menuKey} 사용에 필요한 lydia (${deductionAmount})가 부족합니다. 현재 lydia: ${state.lydia}`,
        )
        return false // 차감 실패 (lydia 부족)
      }
    },

    async refundLydia({ commit, state, getters }, menuKey) {
      console.log(`Attempting to refund Lydia for ${menuKey}`)
      const userId = state.user?.accessToken
      if (!userId) return

      const amount = getters.getDeductionValue(menuKey)
      console.log(`Refunding ${amount} Lydia for ${menuKey}`)

      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, {
        lydia: increment(amount),
      })

      const userSnap = await getDoc(userRef)
      const lydiaAfter = userSnap.data().lydia

      await addDoc(collection(db, 'users', userId, 'lydia_history'), {
        amount,
        createdAt: new Date().toISOString(),
        lydiaAfter,
        menuUsed: menuKey,
        type: 'refund',
      })

      commit('SET_LYDIA', lydiaAfter)
    },

    // 앱 종료 또는 새로고침 전에 사용자 정보 저장 (선택 사항)
    // 현재 방식에서는 lydia 차감 시 Firestore에 바로 반영되므로 이 액션의 중요성이 줄어듭니다.
    // 필요한 경우 여기에 추가적인 저장 로직을 구현합니다.
    async saveUserBeforeUnload({ dispatch, state }) {
      // 이 액션은 주로 페이지를 벗어나기 전에 현재 상태를 서버에 저장하는 용도였습니다.
      // lydia 차감은 이미 deductLydia 액션에서 Firestore에 반영하고 있으므로,
      // 여기서 추가로 저장할 데이터가 있다면 구현합니다.
      // 예: 마지막 접속 시간만 업데이트
      if (state.user && state.user.accessToken) {
        try {
          const userRef = doc(db, 'users', state.user.accessToken)
          await setDoc(
            userRef,
            {
              lastLeftAt: serverTimestamp(), // 서버 타임스탬프 사용
            },
            { merge: true },
          ) // 기존 데이터에 추가/업데이트
          console.log('saveUserBeforeUnload: 마지막 접속 시간 업데이트 성공')
        } catch (error) {
          console.error(
            'saveUserBeforeUnload 실행 중 Firestore 저장 실패:',
            error,
          )
        }
      } else {
        console.log(
          'saveUserBeforeUnload 액션 실행. 사용자 또는 accessToken이 없어 저장하지 않습니다.',
        )
      }
    },

    // 사용자 로그아웃 (localStorage에서 accessToken 제거)
    // 이 액션은 사용자가 명시적으로 계정을 초기화하고 싶을 때 사용합니다.
    async logout({ commit }) {
      localStorage.removeItem('accessToken') // localStorage에서 액세스 코드 제거
      commit('CLEAR_USER') // 사용자 정보 초기화
      console.log('사용자 로그아웃 (localStorage에서 accessToken 제거)')
      // 로그아웃 후 정보 입력 또는 초기 화면으로 리다이렉트하는 것은 라우터 레벨에서 처리합니다.
    },

    // ... 다른 actions 속성이 있다면 유지
  },
})

export default store
